'use client';

import { useState } from 'react';

const INITIAL_STATE = {
  billAmount: 100,
  tipPercent: 18,
  people: 1,
};

const PRESETS = {
  conservative: {
    billAmount: 50,
    tipPercent: 15,
    people: 2,
  },
  moderate: {
    billAmount: 100,
    tipPercent: 18,
    people: 1,
  },
  aggressive: {
    billAmount: 200,
    tipPercent: 25,
    people: 4,
  },
};

export default function CalculatorTip() {
  const [billAmount, setBillAmount] = useState(INITIAL_STATE.billAmount);
  const [tipPercent, setTipPercent] = useState(INITIAL_STATE.tipPercent);
  const [people, setPeople] = useState(INITIAL_STATE.people);
  const [isCalculating, setIsCalculating] = useState(false);

  const handleChange = (setter: (v: number) => void, value: number) => {
    setter(value);
    setIsCalculating(true);
    setTimeout(() => setIsCalculating(false), 800);
  };

  const tipAmount = (billAmount * tipPercent) / 100;
  const total = billAmount + tipAmount;
  const perPerson = total / people;

  const handleReset = () => {
    setBillAmount(INITIAL_STATE.billAmount);
    setTipPercent(INITIAL_STATE.tipPercent);
    setPeople(INITIAL_STATE.people);
  };

  const handlePreset = (preset: keyof typeof PRESETS) => {
    const values = PRESETS[preset];
    setBillAmount(values.billAmount);
    setTipPercent(values.tipPercent);
    setPeople(values.people);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200">
        {isCalculating && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-emerald-700 font-semibold">Calculating...</span>
            </div>
          </div>
        )}

        {/* Preset and Reset Buttons */}
        <div className="mb-6 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button
              onClick={() => handlePreset('conservative')}
              className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
            >
              Conservative
            </button>
            <button
              onClick={() => handlePreset('moderate')}
              className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
            >
              Moderate
            </button>
            <button
              onClick={() => handlePreset('aggressive')}
              className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
            >
              Aggressive
            </button>
          </div>
          <button
            onClick={handleReset}
            className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
          >
            Reset to Default
          </button>
        </div>

        <div className="space-y-6 mb-8">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Bill Amount</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">$</span>
              <input
                type="number"
                value={billAmount}
                onChange={(e) => handleChange(setBillAmount, Number(e.target.value))}
                className="w-full pl-7 pr-3 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Tip Percentage</label>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[15, 18, 20, 25].map((percent) => (
                <button
                  key={percent}
                  onClick={() => handleChange(setTipPercent, percent)}
                  className={`py-2 rounded-lg font-semibold transition-all ${
                    tipPercent === percent
                      ? 'bg-emerald-500 text-white' :'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {percent}%
                </button>
              ))}
            </div>
            <div className="relative">
              <input
                type="number"
                value={tipPercent}
                onChange={(e) => handleChange(setTipPercent, Number(e.target.value))}
                className="w-full pl-3 pr-10 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">%</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Split Between</label>
            <div className="relative">
              <input
                type="number"
                value={people}
                onChange={(e) => handleChange(setPeople, Math.max(1, Number(e.target.value)))}
                min={1}
                className="w-full pl-3 pr-16 py-3 border-2 border-slate-200 rounded-lg focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 outline-none transition-all font-semibold text-slate-900"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">people</span>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 rounded-xl p-6 text-white text-center">
            <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">Per Person</div>
            <div className="text-5xl font-bold">${perPerson.toFixed(2)}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Tip Amount</div>
              <div className="text-xl font-bold text-slate-900">${tipAmount.toFixed(2)}</div>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 text-center">
              <div className="text-xs text-slate-600 mb-1">Total</div>
              <div className="text-xl font-bold text-slate-900">${total.toFixed(2)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}